import type { Texture, FederatedPointerEvent } from 'pixi.js';
import { Assets, BlurFilter, Container, Graphics, Sprite, Text } from 'pixi.js';
import { draw3DButton, drawHeaderBand, drawInsetRect, drawStatusBar, GUGI_SHADOW } from '../rendering/PanelUtils';
import {
  MARGIN, GAP, PAD_M,
  LEFT_W, RIGHT_W, COL_LEFT_X, CONTENT_Y, bottomY, colRightX,
  colCenterX, centerW,
  tabBarH,
  FONT_HEADER, FONT_SUBHEADER, FONT_BODY, FONT_DETAIL,
  TEXT_PRIMARY, TEXT_SECONDARY, TEXT_DIM, TEXT_MUTED,
  PANEL_FILL, INSET_FILL, INSET_RADIUS,
  ACCENT, ACCENT_MED, ACCENT_DIM, ACCENT_DARK,
  drawStandardPanel, computePentagramSlots,
} from '../rendering/LayoutConstants';
import { getViewport, onViewportChange, type ViewportInfo } from '../rendering/Viewport';
import { MobileTabBar } from '../rendering/MobileTabBar';
import { enableLongPress } from '../input/TouchGestures';
import type { GameState, GameStateManager } from './GameStateManager';
import { LabyrinthState } from './LabyrinthState';
import type { UpgradeCategory, UpgradeDef} from '../upgrades/UpgradeData';
import { getUpgradeById } from '../upgrades/UpgradeData';
import type { WeaponDef} from '../upgrades/WeaponData';
import { getWeaponById } from '../upgrades/WeaponData';
import { STAT_NAMES, STAT_INFO } from '../upgrades/StatsSystem';
import {
  createStatRowTooltip, createEmptySlotTooltip, createDeployTooltip,
  createStatBreakdownTooltip, type StatBreakdownLine,
} from '../rendering/TooltipSystem';
import { type ScoredItem, scoreBand } from '../upgrades/Quality';
import { drawRobotBody, drawRobotShadow, drawRobotTurret } from '../rendering/RobotGraphic';
import type { WeaponSlotRenderInfo } from '../labyrinth/Robot';
import {
  INV_CELL, INV_GAP, INV_COLS, WPN_SLOT_SIZE, SLOT_SIZE,
  qColor, drawCategoryIcon, drawWeaponIcon, drawStatIcon,
  buildUpgradeCell as sharedUpgradeCell,
  buildWeaponCell as sharedWeaponCell,
  createUpgradeTooltip, createWeaponTooltip, positionTooltip,
} from '../rendering/InventoryUI';
import { Labyrinth, type DoorDirection, Tile } from '../labyrinth/Labyrinth';
import { loadMapFromSVG } from '../labyrinth/MapLoader';
import { createSettingsButton } from '../audio/SettingsOverlay';
import { buildTraderOverlay, type TraderOverlayResult } from '../rendering/TraderUI';
import { BuildingState } from './BuildingState';
import { buildForgeOverlay, type ForgeOverlayResult } from '../rendering/ForgeUI';
import { buildAchievementsOverlay, type AchievementsOverlayResult } from '../rendering/AchievementsUI';
import { buildAchievementToastOverlay, type AchievementToastOverlay } from '../rendering/AchievementToast';
import { createScrollBar, SCROLLBAR_WIDTH, type ScrollBarHandle } from '../rendering/ScrollBar';
import { attachScrollDrag, type ScrollDragHandle } from '../rendering/ScrollablePanel';

// ─── Constants ────────────────────────────────────────────
const BASE_ROBOT_SIZE = 160;

// Drag source types
type DragSource =
  | { type: 'upgradeInventory' }
  | { type: 'upgradeSlot'; category: UpgradeCategory }
  | { type: 'weaponInventory' }
  | { type: 'weaponSlot'; index: number };

// ─── BaseState ────────────────────────────────────────────
export class BaseState implements GameState {
  private container = new Container();
  private uiContainer = new Container();  // holds all UI elements (for fade-in)
  private manager: GameStateManager;
  private transitionIn: boolean;

  private dragGhost: Container | null = null;
  private dragItem: ScoredItem | null = null;
  private dragItemKind: 'upgrade' | 'weapon' | null = null;
  private dragSource: DragSource | null = null;
  // Pickup feedback: pop-in scale tween + lag-follow cursor.
  private dragGhostTargetX = 0;
  private dragGhostTargetY = 0;
  private dragGhostPopTime = 0;
  private slotHitAreas: { category: UpgradeCategory; x: number; y: number; w: number; h: number }[] = [];
  private weaponSlotHitAreas: { index: number; x: number; y: number; w: number; h: number }[] = [];
  private inventoryHitArea: { x: number; y: number; w: number; h: number } | null = null;
  private tooltip: Container | null = null;
  private tooltipDismissDisposer: (() => void) | null = null;
  private logoDetail: Sprite | null = null;
  private logoBaseScale = 1;
  private logoTime = 0;
  private traderOverlay: TraderOverlayResult | null = null;
  private traderBtn: Container | null = null;
  private traderTimerText: Text | null = null;
  private forgeOverlay: ForgeOverlayResult | null = null;
  private achievementsOverlay: AchievementsOverlayResult | null = null;
  private toastOverlay: AchievementToastOverlay | null = null;
  private statsPanelHeight = 0;
  private minimapPanelHeight = 0;
  private robotStatsPanelHeight = 0;

  // Inventory drawer drag state (mobile ROBOT tab)
  private drawerDrag: {
    startY: number;
    startH: number;
    minH: number;
    maxH: number;
    dragged: boolean;
    previewLine: Graphics | null;
    panelX: number;
    panelW: number;
  } | null = null;

  // Inventory scroll state
  private invScrollOffset = 0;
  private invContent: Container | null = null;
  private invScrollBar: ScrollBarHandle | null = null;
  private invMaxScroll = 0;
  private invScrollDrag: ScrollDragHandle | null = null;

  // Background animation state
  private circuitGfx: Graphics | null = null;
  private circuitSegments: { x1: number; y1: number; x2: number; y2: number; tier: number }[] = [];
  private scanLine: Graphics | null = null;
  private pulseGfx: Graphics | null = null;
  private bgTime = 0;
  private circuitBaseAlpha = 1;
  private pulses: { segIdx: number; t: number; speed: number; tier: number }[] = [];

  // Fade-in transition state
  private fadeInTime = 0;
  private static readonly FADE_IN_DURATION = 0.6;

  // Mobile layout state (survives rebuildUI so tab selection persists across rebuilds)
  private static mobileActiveTab: 0 | 1 | 2 = 0;
  // Inventory drawer state (ROBOT tab). Height is in px; null = use default (~50%).
  // Collapsed drawer shows only the drag handle.
  private static invDrawerHeight: number | null = (() => {
    try { const v = localStorage.getItem('base.invDrawer.h'); return v != null ? Number(v) : null; }
    catch { return null; }
  })();
  private static invDrawerCollapsed: boolean = (() => {
    try { const v = localStorage.getItem('base.invDrawer.collapsed'); return v === '1'; }
    catch { return false; }
  })();
  private static persistInvDrawer() {
    try {
      if (BaseState.invDrawerHeight != null)
        localStorage.setItem('base.invDrawer.h', String(BaseState.invDrawerHeight));
      localStorage.setItem('base.invDrawer.collapsed', BaseState.invDrawerCollapsed ? '1' : '0');
    } catch { /* ignore */ }
  }
  private vp!: ViewportInfo;
  private vpUnsub: (() => void) | null = null;

  constructor(manager: GameStateManager, transitionIn = false) {
    this.manager = manager;
    this.transitionIn = transitionIn;
  }

  enter() {
    // Collect passive credits from Credit Generator building
    const passiveCredits = this.manager.buildings.collectPassiveCredits();
    if (passiveCredits > 0) {
      this.manager.money += passiveCredits;
    }

    const app = this.manager.app;
    this.vp = getViewport(app);
    if (!this.vpUnsub) {
      this.vpUnsub = onViewportChange(() => { this.rebuildUI(); });
    }
    app.stage.addChild(this.container);

    const bg = new Graphics();
    bg.rect(0, 0, app.screen.width, app.screen.height);
    bg.fill(0x080a10);
    this.container.addChild(bg);

    // Two-level grid overlay — minor (40px) + major (80px)
    const grid = new Graphics();
    const W = app.screen.width, H = app.screen.height;
    for (let x = 0; x < W; x += 40) {
      grid.moveTo(x, 0); grid.lineTo(x, H);
    }
    for (let y = 0; y < H; y += 40) {
      grid.moveTo(0, y); grid.lineTo(W, y);
    }
    grid.stroke({ color: 0x111822, width: 1 });
    for (let x = 0; x < W; x += 80) {
      grid.moveTo(x, 0); grid.lineTo(x, H);
    }
    for (let y = 0; y < H; y += 80) {
      grid.moveTo(0, y); grid.lineTo(W, y);
    }
    grid.stroke({ color: 0x16202e, width: 1 });
    this.container.addChild(grid);

    // Top accent line
    const topLine = new Graphics();
    topLine.moveTo(0, 0); topLine.lineTo(app.screen.width, 0);
    topLine.stroke({ color: ACCENT, width: 2 });
    this.container.addChild(topLine);

    this.buildCircuitBackground(W, H);

    // Logo layer — reserved synchronously so async-loaded sprites stay
    // beneath the interactive uiContainer regardless of load timing.
    const logoLayer = new Container();
    logoLayer.alpha = 0.2;
    this.container.addChild(logoLayer);

    // Logo: two layers — red base + animated yellow detail
    void Promise.all([
      Assets.load<Texture>('/logo-base.svg'),
      Assets.load<Texture>('/logo-detail.svg'),
    ]).then(([baseTex, detailTex]) => {
      const logoX = COL_LEFT_X, logoWidth = LEFT_W;
      const scale = logoWidth / baseTex.width;

      // SVG viewBox → texture coordinate mapping
      const svgW = 988, svgH = 395;
      const texScaleX = baseTex.width / svgW;
      const texScaleY = baseTex.height / svgH;

      const logoBase = new Sprite(baseTex);
      logoBase.width = logoWidth;
      logoBase.height = baseTex.height * scale;
      logoBase.position.set(logoX, 8);
      logoLayer.addChild(logoBase);

      const detail = new Sprite(detailTex);
      detail.scale.set(scale);
      // Pivot at center of yellow star in texture coords (SVG 946,87 mapped to tex)
      const pivotX = 932 * texScaleX;
      const pivotY = 95 * texScaleY;
      detail.pivot.set(pivotX, pivotY);
      // Position so pivot aligns with same point in the base logo
      detail.position.set(logoX + pivotX * scale, 8 + pivotY * scale);
      logoLayer.addChild(detail);
      this.logoDetail = detail;
      this.logoBaseScale = scale;
    });

    void this.manager.sound.playMusic('/sound/bg-music-base.mp3');

    // UI elements go into uiContainer for fade-in transition support
    this.container.addChild(this.uiContainer);
    if (this.transitionIn) {
      this.uiContainer.alpha = 0;
      this.fadeInTime = 0;
    }

    // Achievement toast overlay — drains `pendingToasts` into slide-in cards.
    // Attached to `container` (not `uiContainer`) so it sits above the fade-in
    // and any overlay opened on top.
    this.toastOverlay = buildAchievementToastOverlay(
      this.manager.achievements, this.manager.app.screen.width,
    );
    this.container.addChild(this.toastOverlay.container);

    // Temporarily swap addChild target so build methods add to uiContainer
    const realContainer = this.container;
    this.container = this.uiContainer;

    if (this.vp.mode === 'mobile') {
      this.buildMobileLayout();
    } else {
      this.buildStatsPanel();
      this.computeMinimapHeight();
      void this.buildMinimap();
      this.buildRobotStatsPanel();
      this.buildDeployButton();
      this.buildEquipmentArea();
      this.buildInventoryGrid();
      if (this.manager.buildings.getBuildingLevel('tradehub') > 0) {
        this.buildTraderButton();
      }
      this.buildBuildingButtons();
    }

    // Settings button — top-right corner
    createSettingsButton(this.container, app, this.manager.sound, app.screen.width - 46, 10, this.manager);

    // Restore container reference
    this.container = realContainer;

    this.container.eventMode = 'static';
    this.container.on('pointermove', (e: FederatedPointerEvent) => this.onDragMove(e));
    this.container.on('pointerup', (e: FederatedPointerEvent) => this.onDragEnd(e));
    this.container.on('pointerupoutside', (e: FederatedPointerEvent) => this.onDragEnd(e));
  }

  // ─── Mobile Layout ──────────────────────────────────────
  private buildMobileLayout() {
    const vp = this.vp;
    const app = this.manager.app;
    const headerH = 48 + vp.safeTop;
    const barH = tabBarH(vp);
    const contentX = MARGIN;
    const contentY = headerH;
    const contentW = vp.w - MARGIN * 2;
    const contentH = vp.h - headerH - barH - MARGIN;
    const centerX = vp.w / 2;

    // Compact header strip: credits + level (right-aligned)
    const stats = this.manager.stats;
    const hdr = new Text({
      text: `LV.${stats.level}   Cr ${this.manager.money.toLocaleString()}`,
      style: { ...FONT_SUBHEADER, fill: ACCENT, dropShadow: GUGI_SHADOW },
    });
    hdr.position.set(MARGIN, vp.safeTop + 14);
    this.container.addChild(hdr);

    const tab = BaseState.mobileActiveTab;

    if (tab === 0) {
      // ROBOT tab — split view: robot/slots on top, inventory drawer at bottom.
      // Drawer is draggable (resize) and collapsible so the player can trade
      // screen real-estate between the pentagram and the inventory grid.
      const HANDLE_H = 28;
      const MIN_DRAWER = HANDLE_H;                       // collapsed = handle only
      const MAX_DRAWER = Math.floor(contentH * 0.8);     // leave room for robot
      const DEFAULT_DRAWER = Math.floor(contentH * 0.5); // 50/50 split

      let drawerH = BaseState.invDrawerCollapsed
        ? MIN_DRAWER
        : (BaseState.invDrawerHeight ?? DEFAULT_DRAWER);
      drawerH = Math.max(MIN_DRAWER, Math.min(MAX_DRAWER, drawerH));

      const drawerY = contentY + contentH - drawerH;

      // Top: deploy button at top, pentagram centered in remaining space.
      this.buildDeployButton({ cx: centerX, cy: contentY + 30 });

      // Budget the top area so the pentagram + weapon row both fit above the
      // drawer. Vertical layout (top→bottom):
      //   deploy button      ~50 px
      //   pentagram top slot   SLOT_SIZE/2 above cy
      //   pentagram bottom    0.809·r + SLOT_SIZE/2 + 14 (name label)
      //   gap                 wpnGap
      //   weapon row          14 (label) + WPN_SLOT_SIZE + 14 (name)
      //   bottom pad           8
      const topAreaH = drawerY - contentY;
      const DEPLOY_AREA = 56;
      const WPN_GAP = 22;
      const WPN_ROW_H = 14 + WPN_SLOT_SIZE + 14;
      const PENT_PAD_ABOVE = SLOT_SIZE / 2;
      const PENT_PAD_BELOW = SLOT_SIZE / 2 + 14;
      const vBudget = topAreaH - DEPLOY_AREA - PENT_PAD_ABOVE - PENT_PAD_BELOW - WPN_GAP - WPN_ROW_H - 8;
      // Pentagram vertical extent = r + 0.809·r = 1.809·r
      const rV = Math.floor(vBudget / 1.809);
      const rH = Math.floor(contentW / 2) - (SLOT_SIZE / 2 + 20);
      // Vertical budget sets base radius; if there's horizontal slack, stretch
      // the pentagram sideways so the slots land closer to the screen edges
      // instead of bunched in the middle.
      const radius = Math.max(50, Math.min(140, rV));
      const robotSize = Math.min(120, Math.round(radius * 0.85));
      // Max horizontal slot offset from pentagram is sin(72°)·radius ≈ 0.951·r.
      // Scale X so the widest slots reach rH.
      const xScale = Math.max(1, rH / (0.951 * radius));

      // Place pentagram center so the top slot sits just below the deploy
      // button and the weapon row lands above the drawer.
      const equipCy = contentY + DEPLOY_AREA + PENT_PAD_ABOVE + radius;
      this.buildEquipmentArea({
        cx: centerX,
        cy: equipCy,
        radius,
        robotSize,
        wpnGap: WPN_GAP,
        xScale,
      });

      // Inventory drawer — draggable handle + inventory grid.
      this.buildInventoryDrawer({
        x: contentX,
        y: drawerY,
        w: contentW,
        h: drawerH,
        handleH: HANDLE_H,
        minH: MIN_DRAWER,
        maxH: MAX_DRAWER,
        collapsed: BaseState.invDrawerCollapsed,
      });
    } else if (tab === 1) {
      // STATS tab — player stats + robot stats stacked
      this.buildStatsPanel({ x: contentX, y: contentY, w: contentW });
      const below = contentY + this.statsPanelHeight + GAP;
      this.buildRobotStatsPanel({ x: contentX, y: below, w: contentW });
    } else {
      // MAP tab — full-width minimap of the labyrinth
      this.computeMinimapHeight({ y: contentY, w: contentW, maxH: contentH });
      void this.buildMinimap({ x: contentX, y: contentY, w: contentW });
    }

    // Trader / buildings / forge — compact buttons in the top bar (both tabs)
    this.buildMobileTopButtons();

    // Bottom tab bar
    const bar = new MobileTabBar(
      ['ROBOT', 'STATS', 'MAP'],
      (idx) => {
        if (idx === BaseState.mobileActiveTab) return;
        BaseState.mobileActiveTab = idx as 0 | 1 | 2;
        this.rebuildUI();
      },
      { width: vp.w, height: 64 },
    );
    bar.position.set(0, vp.h - barH);
    bar.setActive(tab);
    bar.setBadge(1, this.manager.stats.unspentPoints > 0);
    this.container.addChild(bar);

    // Silence unused-import lint — app var reserved for future use (tooltips, overlays)
    void app;
  }

  private buildCircuitBackground(W: number, H: number) {
    // Seeded xorshift32 RNG for deterministic pattern
    let seed = 0xDEADBEEF;
    const rand = () => {
      seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5;
      return (seed >>> 0) / 0xFFFFFFFF;
    };

    const GRID = 80;
    const cols = Math.ceil(W / GRID) + 1;
    const rows = Math.ceil(H / GRID) + 1;

    // Three tiers: [lineWidth, glowWidth, color, alpha, glowAlpha]
    const TIERS: [number, number, number, number, number][] = [
      [2.5, 12, 0xDD2233, 0.38, 0.07],
      [1.5,  8, 0xBB1122, 0.26, 0.05],
      [1.0,  5, 0x881122, 0.16, 0.03],
    ];

    // Generate segments: array of {x1,y1,x2,y2,tier}
    type Seg = { x1: number; y1: number; x2: number; y2: number; tier: number };
    const segments: Seg[] = [];
    const viaPads = new Map<string, number>(); // "x,y" -> max tier index (0=thickest)

    const addSeg = (x1: number, y1: number, x2: number, y2: number, tier: number) => {
      segments.push({ x1, y1, x2, y2, tier });
      const key1 = `${x1},${y1}`, key2 = `${x2},${y2}`;
      const prev1 = viaPads.get(key1) ?? 999;
      const prev2 = viaPads.get(key2) ?? 999;
      if (tier < prev1) viaPads.set(key1, tier);
      if (tier < prev2) viaPads.set(key2, tier);
    };

    // Walk traces starting from random grid nodes
    const traceCount = Math.floor(cols * rows * 0.4);
    for (let t = 0; t < traceCount; t++) {
      const tier = rand() < 0.15 ? 0 : rand() < 0.35 ? 1 : 2;
      let cx = Math.floor(rand() * cols) * GRID;
      let cy = Math.floor(rand() * rows) * GRID;
      const len = 2 + Math.floor(rand() * 5);
      let dir = Math.floor(rand() * 4); // 0=right,1=down,2=left,3=up
      const dx = [1, 0, -1, 0] as const;
      const dy = [0, 1, 0, -1] as const;

      for (let step = 0; step < len; step++) {
        const nx = cx + dx[dir]! * GRID;
        const ny = cy + dy[dir]! * GRID;
        if (nx < -GRID || ny < -GRID || nx > W + GRID || ny > H + GRID) break;
        addSeg(cx, cy, nx, ny, tier);
        cx = nx; cy = ny;
        // Occasionally turn
        if (rand() < 0.4) {
          dir = rand() < 0.5
            ? (dir + 1) % 4
            : (dir + 3) % 4;
        }
      }
    }

    // Draw glow pass then core pass
    const g = new Graphics();
    g.alpha = 1;

    for (const { x1, y1, x2, y2, tier } of segments) {
      const [, gw, col, , ga] = TIERS[tier]!;
      g.moveTo(x1, y1); g.lineTo(x2, y2);
      g.stroke({ color: col, width: gw, alpha: ga });
    }

    for (const { x1, y1, x2, y2, tier } of segments) {
      const [lw, , col, ca] = TIERS[tier]!;
      g.moveTo(x1, y1); g.lineTo(x2, y2);
      g.stroke({ color: col, width: lw, alpha: ca });
    }

    // Via pads at intersections
    for (const [key, tier] of viaPads) {
      const [px, py] = key.split(',').map(Number) as [number, number];
      const [lw, gw, col, ca, ga] = TIERS[tier]!;
      const r = lw + 1.5;
      // Glow halo
      g.circle(px, py, r + gw / 2);
      g.fill({ color: col, alpha: ga * 1.5 });
      // Outer ring
      g.circle(px, py, r + 1);
      g.stroke({ color: col, width: 0.8, alpha: ca * 0.6 });
      // Inner dot
      g.circle(px, py, r * 0.55);
      g.fill({ color: col, alpha: ca });
    }

    this.container.addChild(g);
    this.circuitGfx = g;
    this.circuitSegments = segments;
    this.circuitBaseAlpha = g.alpha;

    // Scan line — a faint vertical sweep
    const scan = new Graphics();
    scan.alpha = 0;
    this.container.addChild(scan);
    this.scanLine = scan;

    // Pulse layer — energy dots traveling along traces
    const pulse = new Graphics();
    this.container.addChild(pulse);
    this.pulseGfx = pulse;

    // Seed initial pulses from random segments
    const PULSE_COUNT = 12;
    for (let i = 0; i < PULSE_COUNT; i++) {
      const segIdx = Math.floor(rand() * segments.length);
      this.pulses.push({
        segIdx,
        t: rand(),
        speed: 0.15 + rand() * 0.25,
        tier: segments[segIdx]!.tier,
      });
    }
  }

  /** Pre-compute minimap panel height synchronously so later panels can position after it. */
  private computeMinimapHeight(opts?: { y?: number; w?: number; maxH?: number }) {
    const app = this.manager.app;
    const panelW = opts?.w ?? LEFT_W;
    const HEADER_H = 28;
    const MAP_INNER_PAD = PAD_M;
    const MAP_W = panelW - MAP_INNER_PAD * 2;
    const panelY = opts?.y ?? (CONTENT_Y + this.statsPanelHeight + GAP);
    const maxPanelH = opts?.maxH ?? (bottomY(app.screen.height) - GAP - panelY);
    const idealMapH = Math.round(MAP_W * 1.4);
    const MAP_H = Math.min(idealMapH, maxPanelH - HEADER_H - MAP_INNER_PAD * 2);
    this.minimapPanelHeight = MAP_H + HEADER_H + MAP_INNER_PAD * 2;
  }

  private async buildMinimap(opts?: { x?: number; y?: number; w?: number }) {
    // If we have saved exploration but no labyrinth yet, load the map now
    if (!this.manager.labyrinth && this.manager.exploration) {
      const mapData = await loadMapFromSVG('/maps/placeholder-map.svg');
      const labyrinth = new Labyrinth(
        mapData.width,
        mapData.height,
        mapData.tiles,
        mapData.baseTile,
        mapData.doors,
      );
      this.manager.initExploration(labyrinth);
    }

    const lab = this.manager.labyrinth;
    const exploration = this.manager.exploration;

    const panelX = opts?.x ?? COL_LEFT_X;
    const panelW = opts?.w ?? LEFT_W;
    const HEADER_H = 28;
    const MAP_INNER_PAD = PAD_M;
    const MAP_W = panelW - MAP_INNER_PAD * 2;

    // Position below stats panel — height already computed in computeMinimapHeight()
    const panelY = opts?.y ?? (CONTENT_Y + this.statsPanelHeight + GAP);
    const panelH = this.minimapPanelHeight;
    const MAP_H = panelH - HEADER_H - MAP_INNER_PAD * 2;

    const panel = new Container();
    panel.position.set(panelX, panelY);
    this.container.addChild(panel);

    // Panel background + inset screen bezel
    const bg = new Graphics();
    drawStandardPanel(bg, 0, 0, panelW, panelH);
    bg.roundRect(MAP_INNER_PAD - 3, HEADER_H + MAP_INNER_PAD - 3, MAP_W + 6, MAP_H + 6, 3);
    bg.stroke({ color: 0x1a2a3a, width: 1 });
    panel.addChild(bg);

    // Status text inside panel header
    const statusParts: string[] = [`Lv.${this.manager.stats.level}`];
    if (exploration) {
      const total = this.countWalkableTiles();
      const pct = total > 0 ? Math.round((exploration.visited.size / total) * 100) : 0;
      statusParts.push(`Map: ${pct}%`, `Frontier: ${exploration.frontier.size}`);
    }
    const statusLine = new Text({
      text: statusParts.join('  |  '),
      style: { ...FONT_DETAIL, fill: TEXT_SECONDARY },
    });
    statusLine.anchor.set(0.5, 0);
    statusLine.position.set(panelW / 2, MAP_INNER_PAD);
    panel.addChild(statusLine);

    // Minimap rendering area
    if (!lab || !exploration) return;

    // Compute bounding box of revealed tiles
    let minX = lab.width, minY = lab.height, maxX = 0, maxY = 0;
    let hasRevealed = false;
    for (let y = 0; y < lab.height; y++) {
      for (let x = 0; x < lab.width; x++) {
        if (exploration.revealed[y]![x]) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
          hasRevealed = true;
        }
      }
    }

    if (!hasRevealed) return;

    // Add 1-tile padding around the bounding box
    minX = Math.max(0, minX - 1);
    minY = Math.max(0, minY - 1);
    maxX = Math.min(lab.width - 1, maxX + 1);
    maxY = Math.min(lab.height - 1, maxY + 1);

    const spanX = maxX - minX + 1;
    const spanY = maxY - minY + 1;
    const scale = Math.min(MAP_W / spanX, MAP_H / spanY);

    // Center the map in the rendering area
    const renderedW = spanX * scale;
    const renderedH = spanY * scale;
    const offsetX = MAP_INNER_PAD + (MAP_W - renderedW) / 2;
    const offsetY = MAP_INNER_PAD + HEADER_H + (MAP_H - renderedH) / 2;

    const mapGfx = new Graphics();
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const px = offsetX + (x - minX) * scale;
        const py = offsetY + (y - minY) * scale;

        if (!exploration.revealed[y]![x]) continue;

        const tile = lab.tiles[y]![x];
        let color: number;
        if (tile === Tile.Wall) {
          color = 0x3a1018;
        } else if (tile === Tile.Base || tile === Tile.Door) {
          color = 0x1a3a30;
        } else {
          // Floor — shade by visited vs frontier
          const key = `${x},${y}`;
          if (exploration.visited.has(key)) {
            color = 0x2a3040;
          } else if (exploration.frontier.has(key)) {
            color = 0x3a4a30;
          } else {
            color = 0x202830;
          }
        }

        mapGfx.rect(px, py, scale, scale);
        mapGfx.fill(color);
      }
    }

    // Draw base marker
    const bx = offsetX + (lab.baseTile.x - minX) * scale;
    const by = offsetY + (lab.baseTile.y - minY) * scale;
    const markerSize = Math.max(3, scale * 1.5);
    mapGfx.rect(bx - markerSize / 2 + scale / 2, by - markerSize / 2 + scale / 2, markerSize, markerSize);
    mapGfx.fill(0xDD2233);

    panel.addChild(mapGfx);
  }

  private buildDeployButton(opts?: { cx?: number; cy?: number }) {
    const app = this.manager.app;
    const exploration = this.manager.exploration;
    const centerX = opts?.cx ?? (colCenterX() + centerW(app.screen.width) / 2);
    const centerY = opts?.cy ?? 100;

    const deployContainer = new Container();
    deployContainer.position.set(centerX, centerY);
    this.container.addChild(deployContainer);

    // Label above — still communicates the action ("deploy from direction X")
    const actionLabel = exploration && exploration.frontier.size > 0
      ? 'CONTINUE FROM:' : 'DEPLOY FROM:';
    const doorLabel = new Text({ text: actionLabel, style: { ...FONT_DETAIL, fill: TEXT_SECONDARY } });
    doorLabel.anchor.set(0.5, 0.5);
    doorLabel.position.set(0, -30);
    deployContainer.addChild(doorLabel);

    // Four-segment direction bar — press a segment to deploy from that side.
    // Single horizontal row so the button footprint stays compact and does not
    // intrude into the pentagram below.
    const barW = 240, segH = 36;
    const segW = barW / 4;
    const segments: { dir: DoorDirection; label: string; arrow: 'up' | 'down' | 'left' | 'right' }[] = [
      { dir: 'west',  label: 'W', arrow: 'left'  },
      { dir: 'north', label: 'N', arrow: 'up'    },
      { dir: 'south', label: 'S', arrow: 'down'  },
      { dir: 'east',  label: 'E', arrow: 'right' },
    ];

    for (let i = 0; i < segments.length; i++) {
      const s = segments[i]!;
      const seg = new Container();
      const sx = -barW / 2 + i * segW;
      seg.position.set(sx, -segH / 2);

      const segBg = new Graphics();
      draw3DButton(segBg, segW - 2, segH, ACCENT_MED);
      seg.addChild(segBg);

      // Arrow icon (small triangle) + letter label side-by-side
      const ax = 12, ay = segH / 2;
      const arrowGfx = new Graphics();
      const aSize = 6;
      arrowGfx.moveTo(0, -aSize);
      arrowGfx.lineTo(aSize, aSize);
      arrowGfx.lineTo(-aSize, aSize);
      arrowGfx.closePath();
      arrowGfx.fill(0xffffff);
      arrowGfx.position.set(ax, ay);
      arrowGfx.rotation = s.arrow === 'up' ? 0
        : s.arrow === 'right' ? Math.PI / 2
        : s.arrow === 'down' ? Math.PI
        : -Math.PI / 2;
      seg.addChild(arrowGfx);

      const letter = new Text({ text: s.label, style: { ...FONT_HEADER, fill: 0xffffff, dropShadow: GUGI_SHADOW } });
      letter.anchor.set(0.5);
      letter.position.set(segW / 2 + 6, segH / 2);
      seg.addChild(letter);

      seg.eventMode = 'static'; seg.cursor = 'pointer';
      seg.on('pointerover', () => { segBg.tint = 0xcccccc; });
      seg.on('pointerout',  () => { segBg.tint = 0xffffff; });
      seg.on('pointerdown', () => {
        this.manager.selectedDoor = s.dir;
        this.manager.switchTo(new LabyrinthState(this.manager));
      });
      seg.on('pointerenter', () => {
        const ups = this.manager.upgrades;
        const bldg = this.manager.buildings;
        const computed = this.manager.stats.computeRobotStats(ups.getComputedStats(), {
          damageMult: bldg.getDamageMultiplier(),
          damageReduction: bldg.getDamageReductionBonus(),
          detectionBonus: bldg.getDetectionBonus(),
          visionBonus: bldg.getVisionBonus(),
        });
        const weaponCount = ups.getEquippedWeapons().filter(w => w !== null).length;
        const { tip, width, height } = createDeployTooltip(
          computed, weaponCount, ups.weaponSlots, this.manager.getExplorationPct(),
        );
        const gx = centerX - barW / 2;
        const gy = centerY + segH / 2 + 4;
        this.showGenericTooltip(tip, width, height, gx, gy);
      });
      seg.on('pointerleave', () => this.hideTooltip());

      deployContainer.addChild(seg);
    }
  }

  // ─── Equipment Area ─────────────────────────────────────
  private buildEquipmentArea(opts?: { cx?: number; cy?: number; radius?: number; robotSize?: number; wpnGap?: number; xScale?: number }) {
    const app = this.manager.app;
    const ups = this.manager.upgrades;
    const cx = opts?.cx ?? (colCenterX() + centerW(app.screen.width) / 2);
    const cy = opts?.cy ?? (app.screen.height / 2 + 10);
    const radius = opts?.radius ?? 200;
    const robotSize = opts?.robotSize ?? BASE_ROBOT_SIZE;
    const wpnGap = opts?.wpnGap ?? 50;
    const xScale = opts?.xScale ?? 1;
    const slotLayout = computePentagramSlots(radius)
      .map(s => ({ ...s, dx: Math.round(s.dx * xScale), label: s.category.toUpperCase() }));

    // Robot graphic
    const rc = new Container();
    rc.position.set(cx, cy);
    const shadowGfx = new Graphics();
    drawRobotShadow(shadowGfx, robotSize);
    shadowGfx.rotation = -Math.PI / 2;
    shadowGfx.filters = [new BlurFilter({ strength: 8, quality: 3 })];
    rc.addChild(shadowGfx);
    const bodyGfx = new Graphics();
    drawRobotBody(bodyGfx, robotSize, 0, 0, false);
    bodyGfx.rotation = -Math.PI / 2;
    rc.addChild(bodyGfx);
    const turretGfx = new Graphics();
    // Build weapon slot render info for turret display
    const slotCount = ups.weaponSlots;
    const weaponRenderSlots: (WeaponSlotRenderInfo | null)[] = [];
    const equippedWeapons = ups.getEquippedWeapons();
    for (let i = 0; i < slotCount; i++) {
      const w = equippedWeapons[i];
      weaponRenderSlots.push(w ? { category: w.def.category, tier: w.def.tier, cooldownProgress: 0 } : null);
    }
    drawRobotTurret(turretGfx, robotSize, 0, ACCENT, weaponRenderSlots);
    turretGfx.rotation = -Math.PI / 2;
    rc.addChild(turretGfx);
    this.container.addChild(rc);

    // Pentagram connection lines (connect every other vertex)
    const lineGfx = new Graphics();
    const pentOrder = [0, 2, 4, 1, 3]; // skip-one pattern forms the star
    const dashLen = 6, gapLen = 4;
    for (let i = 0; i < 5; i++) {
      const a = slotLayout[pentOrder[i]!]!;
      const b = slotLayout[pentOrder[(i + 1) % 5]!]!;
      const ax = cx + a.dx, ay = cy + a.dy;
      const bx = cx + b.dx, by = cy + b.dy;
      const dx = bx - ax, dy = by - ay;
      const len = Math.hypot(dx, dy);
      const nx = dx / len, ny = dy / len;
      for (let d = 0; d < len; d += dashLen + gapLen) {
        const d1 = d, d2 = Math.min(d + dashLen, len);
        lineGfx.moveTo(ax + nx * d1, ay + ny * d1);
        lineGfx.lineTo(ax + nx * d2, ay + ny * d2);
      }
      lineGfx.stroke({ color: 0x1a1520, width: 1.5 });
    }
    this.container.addChild(lineGfx);

    // Upgrade slots
    this.slotHitAreas = [];
    for (const sl of slotLayout) {
      const eq = ups.getEquipped(sl.category);
      const qItem = ups.getEquippedItem(sl.category);
      const sx = cx + sl.dx - SLOT_SIZE / 2;
      const sy = cy + sl.dy - SLOT_SIZE / 2;

      const slotC = new Container();
      slotC.position.set(sx, sy);
      const color = eq ? qColor(eq.item.score) : 0x2a3040;

      const slotBg = new Graphics();
      drawInsetRect(slotBg, 0, 0, SLOT_SIZE, SLOT_SIZE, INSET_RADIUS, INSET_FILL, color);
      // Corner accent brackets
      const cornerColor = eq ? color : ACCENT_DIM;
      const cornerAlpha = eq ? 0.7 : 0.4;
      slotBg.moveTo(1, 6); slotBg.lineTo(1, 1); slotBg.lineTo(6, 1);
      slotBg.stroke({ color: cornerColor, width: 1, alpha: cornerAlpha });
      slotBg.moveTo(SLOT_SIZE - 6, 1); slotBg.lineTo(SLOT_SIZE - 1, 1); slotBg.lineTo(SLOT_SIZE - 1, 6);
      slotBg.stroke({ color: cornerColor, width: 1, alpha: cornerAlpha });
      slotBg.moveTo(1, SLOT_SIZE - 6); slotBg.lineTo(1, SLOT_SIZE - 1); slotBg.lineTo(6, SLOT_SIZE - 1);
      slotBg.stroke({ color: cornerColor, width: 1, alpha: cornerAlpha });
      slotBg.moveTo(SLOT_SIZE - 6, SLOT_SIZE - 1); slotBg.lineTo(SLOT_SIZE - 1, SLOT_SIZE - 1); slotBg.lineTo(SLOT_SIZE - 1, SLOT_SIZE - 6);
      slotBg.stroke({ color: cornerColor, width: 1, alpha: cornerAlpha });
      slotC.addChild(slotBg);

      if (eq && qItem) {
        const iconSmall = new Graphics();
        drawCategoryIcon(iconSmall, sl.category, 20, color);
        iconSmall.position.set(2, 2); iconSmall.scale.set(0.7);
        slotC.addChild(iconSmall);

        const tierBadge = new Text({ text: `T${eq.def.tier}`, style: { fontFamily: 'Offside', fontSize: 13, fill: color } });
        tierBadge.anchor.set(1, 0); tierBadge.position.set(SLOT_SIZE - 4, 3);
        slotC.addChild(tierBadge);

        const itemIcon = new Graphics();
        drawCategoryIcon(itemIcon, sl.category, 28, color);
        itemIcon.position.set(SLOT_SIZE / 2 - 14, SLOT_SIZE / 2 - 12);
        slotC.addChild(itemIcon);

        // Score badge
        const qLabel = new Text({
          text: `${scoreBand(eq.item.score).label} · ${eq.item.score}`,
          style: { fontFamily: 'Offside', fontSize: 11, fill: color },
        });
        qLabel.alpha = 0.8;
        qLabel.anchor.set(0.5, 1); qLabel.position.set(SLOT_SIZE / 2, SLOT_SIZE - 2);
        slotC.addChild(qLabel);

        const displayName = eq.item.name;
        const nameLabel = new Text({
          text: displayName.length > 18 ? displayName.slice(0, 17) + '…' : displayName,
          style: { fontFamily: 'Offside', fontSize: 12, fill: color },
        });
        nameLabel.anchor.set(0.5, 0); nameLabel.position.set(SLOT_SIZE / 2, SLOT_SIZE + 2);
        slotC.addChild(nameLabel);

        slotC.eventMode = 'static'; slotC.cursor = 'grab';
        slotC.on('pointerdown', (e: FederatedPointerEvent) => this.startDrag(e, qItem, 'upgrade', { type: 'upgradeSlot', category: sl.category }));
        slotC.on('pointerenter', () => this.showUpgradeTooltip(eq.def, eq.item, sx, sy));
        slotC.on('pointerleave', () => this.hideTooltip());
        enableLongPress(slotC, () => this.showUpgradeTooltip(eq.def, eq.item, sx, sy));
      } else {
        const emptyIcon = new Graphics();
        drawCategoryIcon(emptyIcon, sl.category, 30, 0x2a3040);
        emptyIcon.position.set(SLOT_SIZE / 2 - 15, SLOT_SIZE / 2 - 15); emptyIcon.alpha = 0.5;
        slotC.addChild(emptyIcon);
        const catLabel = new Text({ text: sl.label, style: { ...FONT_DETAIL, fill: TEXT_MUTED } });
        catLabel.anchor.set(0.5, 0); catLabel.position.set(SLOT_SIZE / 2, SLOT_SIZE + 2);
        slotC.addChild(catLabel);

        // Tooltip on empty slot
        slotC.eventMode = 'static'; slotC.cursor = 'help';
        const showEmpty = () => {
          const { tip, width, height } = createEmptySlotTooltip(`Empty ${sl.label} slot`);
          this.showGenericTooltip(tip, width, height, sx, sy);
        };
        slotC.on('pointerenter', showEmpty);
        slotC.on('pointerleave', () => this.hideTooltip());
        enableLongPress(slotC, showEmpty);
      }

      this.container.addChild(slotC);
      this.slotHitAreas.push({ category: sl.category, x: sx, y: sy, w: SLOT_SIZE, h: SLOT_SIZE });
    }

    // Weapon slots
    this.weaponSlotHitAreas = [];
    const ws = ups.weaponSlots;
    // Position weapon slots below the lowest pentagram slot + gap
    const lowestSlotDy = Math.max(...slotLayout.map(s => s.dy));
    const wpnY = cy + lowestSlotDy + SLOT_SIZE / 2 + wpnGap;
    const totalWpnW = ws * (WPN_SLOT_SIZE + 6) - 6;

    const wpnLabel = new Text({ text: 'WEAPONS', style: { ...FONT_SUBHEADER, fill: TEXT_DIM, dropShadow: GUGI_SHADOW } });
    wpnLabel.anchor.set(0.5, 0); wpnLabel.position.set(cx, wpnY - 14);
    this.container.addChild(wpnLabel);

    for (let i = 0; i < ws; i++) {
      const wx = cx - totalWpnW / 2 + i * (WPN_SLOT_SIZE + 6);
      const eq = ups.getEquippedWeapon(i);
      const qItem = ups.getEquippedWeaponItem(i);

      const wSlotC = new Container();
      wSlotC.position.set(wx, wpnY);
      const wColor = eq ? qColor(eq.item.score) : 0x3a2a20;

      const wBg = new Graphics();
      drawInsetRect(wBg, 0, 0, WPN_SLOT_SIZE, WPN_SLOT_SIZE, INSET_RADIUS, INSET_FILL, wColor);
      wSlotC.addChild(wBg);

      if (eq && qItem) {
        const wIcon = new Graphics();
        drawWeaponIcon(wIcon, eq.def.category, 28, wColor);
        wIcon.position.set(WPN_SLOT_SIZE / 2 - 14, WPN_SLOT_SIZE / 2 - 14);
        wSlotC.addChild(wIcon);

        const wTier = new Text({ text: `T${eq.def.tier}`, style: { fontFamily: 'Offside', fontSize: 12, fill: wColor } });
        wTier.anchor.set(1, 0); wTier.position.set(WPN_SLOT_SIZE - 3, 2);
        wSlotC.addChild(wTier);

        const wDisplay = eq.item.name;
        const wName = new Text({
          text: wDisplay.length > 14 ? wDisplay.slice(0, 13) + '…' : wDisplay,
          style: { fontFamily: 'Offside', fontSize: 11, fill: wColor },
        });
        wName.anchor.set(0.5, 0); wName.position.set(WPN_SLOT_SIZE / 2, WPN_SLOT_SIZE + 1);
        wSlotC.addChild(wName);

        wSlotC.eventMode = 'static'; wSlotC.cursor = 'grab';
        wSlotC.on('pointerdown', (e: FederatedPointerEvent) => this.startDrag(e, qItem, 'weapon', { type: 'weaponSlot', index: i }));
        wSlotC.on('pointerenter', () => this.showWeaponTooltip(eq.def, eq.item, wx, wpnY));
        wSlotC.on('pointerleave', () => this.hideTooltip());
        enableLongPress(wSlotC, () => this.showWeaponTooltip(eq.def, eq.item, wx, wpnY));
      } else {
        const emptyLabel = new Text({ text: `WPN\n${i + 1}`, style: { ...FONT_DETAIL, fill: TEXT_MUTED, align: 'center' } });
        emptyLabel.anchor.set(0.5); emptyLabel.position.set(WPN_SLOT_SIZE / 2, WPN_SLOT_SIZE / 2);
        wSlotC.addChild(emptyLabel);

        wSlotC.eventMode = 'static'; wSlotC.cursor = 'help';
        const showEmpty = () => {
          const { tip, width, height } = createEmptySlotTooltip(`Empty WEAPON slot ${i + 1}`);
          this.showGenericTooltip(tip, width, height, wx, wpnY);
        };
        wSlotC.on('pointerenter', showEmpty);
        wSlotC.on('pointerleave', () => this.hideTooltip());
        enableLongPress(wSlotC, showEmpty);
      }

      this.container.addChild(wSlotC);
      this.weaponSlotHitAreas.push({ index: i, x: wx, y: wpnY, w: WPN_SLOT_SIZE, h: WPN_SLOT_SIZE });
    }
  }

  // ─── Stats Panel ────────────────────────────────────────
  private buildStatsPanel(opts?: { x?: number; y?: number; w?: number }) {
    const stats = this.manager.stats;
    const panelX = opts?.x ?? COL_LEFT_X;
    const panelY = opts?.y ?? CONTENT_Y;
    const panelW = opts?.w ?? LEFT_W;
    const rowH = 50, headerH = 96;
    const panel = new Container();
    panel.position.set(panelX, panelY);

    const totalH = headerH + STAT_NAMES.length * rowH + PAD_M;
    const bg = new Graphics();
    drawStandardPanel(bg, 0, 0, panelW, totalH);
    panel.addChild(bg);

    const levelBand = new Graphics();
    drawHeaderBand(levelBand, 0, 4, panelW, ACCENT_DARK, PANEL_FILL, 30);
    panel.addChild(levelBand);
    panel.addChild(new Text({ text: `LEVEL ${stats.level}`, style: { ...FONT_HEADER, fill: ACCENT, dropShadow: GUGI_SHADOW } })).position.set(PAD_M, 8);

    // XP progress bar
    const barX = PAD_M, barY = 40, barW = panelW - PAD_M * 2, barH = 9;
    const xpBar = new Graphics();
    drawStatusBar(xpBar, barX, barY, barW, barH, 3, 0x1a1e28, ACCENT_MED, stats.xpProgress, ACCENT_DIM);
    panel.addChild(xpBar);

    panel.addChild(new Text({ text: `XP: ${stats.totalXp} / ${stats.totalXp + stats.xpToNext}`, style: { ...FONT_DETAIL, fill: TEXT_SECONDARY } })).position.set(PAD_M, 56);

    const creditsText = new Text({ text: `Cr  ${this.manager.money.toLocaleString()}`, style: { ...FONT_BODY, fill: 0xffdd44 } });
    creditsText.position.set(PAD_M, 74);
    panel.addChild(creditsText);

    if (stats.unspentPoints > 0) {
      const pt = new Text({ text: `${stats.unspentPoints} pt${stats.unspentPoints > 1 ? 's' : ''}`, style: { ...FONT_BODY, fill: 0xffcc00 } });
      pt.anchor.set(1, 0); pt.position.set(panelW - PAD_M, 56); panel.addChild(pt);
    }

    for (let i = 0; i < STAT_NAMES.length; i++) {
      const name = STAT_NAMES[i]!; const info = STAT_INFO[name]; const value = stats.stats[name];
      const ry = headerH + i * rowH;

      const statIcon = new Graphics();
      drawStatIcon(statIcon, name, 20, TEXT_PRIMARY);
      statIcon.position.set(PAD_M, ry + 6);
      panel.addChild(statIcon);
      panel.addChild(new Text({ text: info.label, style: { ...FONT_BODY, fill: TEXT_PRIMARY } })).position.set(PAD_M + 24, ry + 6);
      panel.addChild(new Text({ text: `${value}`, style: { fontFamily: 'Offside', fontSize: 17, fill: value > 0 ? ACCENT : TEXT_SECONDARY } })).position.set(220, ry + 6);
      panel.addChild(new Text({ text: info.description, style: { ...FONT_DETAIL, fill: TEXT_DIM } })).position.set(PAD_M, ry + 30);

      // Hoverable row for tooltip — added AFTER text so it sits on top and catches all pointer events
      const rowHit = new Graphics();
      rowHit.rect(0, ry, panelW - 40, rowH);
      rowHit.fill({ color: 0x000000, alpha: 0.001 });
      rowHit.eventMode = 'static'; rowHit.cursor = 'help';
      const panelGlobalX = panelX;
      const panelGlobalY = panelY;
      rowHit.on('pointerenter', () => {
        const { tip, width, height } = createStatRowTooltip(name, value);
        this.showGenericTooltip(tip, width, height, panelGlobalX + panelW, panelGlobalY + ry);
      });
      rowHit.on('pointerleave', () => this.hideTooltip());
      panel.addChild(rowHit);

      if (stats.unspentPoints > 0) {
        const addBtn = new Container();
        const addBg = new Graphics();
        draw3DButton(addBg, 26, 22, ACCENT_DARK, { radius: 3, depth: 2 });
        addBtn.addChild(addBg);
        const addLabel = new Text({ text: '+', style: { fontFamily: 'Offside', fontSize: 17, fill: ACCENT } });
        addLabel.anchor.set(0.5); addLabel.position.set(13, 11); addBtn.addChild(addLabel);
        addBtn.on('pointerover', () => { addBg.tint = 0xbbbbbb; });
        addBtn.on('pointerout',  () => { addBg.tint = 0xffffff; });
        addBtn.position.set(panelW - 38, ry + 4); addBtn.eventMode = 'static'; addBtn.cursor = 'pointer';
        addBtn.on('pointerdown', () => { stats.allocate(name); this.rebuildUI(); });
        panel.addChild(addBtn);
      }
    }
    this.container.addChild(panel);
    this.statsPanelHeight = totalH;
  }

  // ─── Robot Computed Stats Panel ─────────────────────────
  private buildRobotStatsPanel(opts?: { x?: number; y?: number; w?: number }) {
    const ups = this.manager.upgrades;
    const bldg = this.manager.buildings;

    const upgradeStats = ups.getComputedStats();
    const buildingBonuses = {
      damageMult: bldg.getDamageMultiplier(),
      damageReduction: bldg.getDamageReductionBonus(),
      detectionBonus: bldg.getDetectionBonus(),
      visionBonus: bldg.getVisionBonus(),
    };
    const { breakdown } = this.manager.stats.computeRobotStatsWithBreakdown(upgradeStats, buildingBonuses);

    const app = this.manager.app;
    const panelW = opts?.w ?? RIGHT_W;
    const panelX = opts?.x ?? colRightX(app.screen.width);
    const panelY = opts?.y ?? CONTENT_Y;
    const rowH = 18;
    const headerH = 28;
    const pad = 8;

    // Stat rows to display (ordered by importance)
    const statKeys = [
      'maxStructure', 'damageReduction', 'speed', 'detectionRadius',
      'visionRadius', 'maxEnergy', 'energyDrain', 'weaponDamageMult', 'accuracy', 'turnSpeed',
    ];

    const totalH = headerH + statKeys.length * rowH + pad;
    this.robotStatsPanelHeight = totalH;
    const panel = new Container();
    panel.position.set(panelX, panelY);

    const bg = new Graphics();
    drawStandardPanel(bg, 0, 0, panelW, totalH);
    panel.addChild(bg);

    const headerBand = new Graphics();
    drawHeaderBand(headerBand, 0, 4, panelW, ACCENT_DARK, PANEL_FILL, 22);
    panel.addChild(headerBand);
    panel.addChild(new Text({
      text: 'ROBOT STATS',
      style: { ...FONT_SUBHEADER, fill: TEXT_PRIMARY, dropShadow: GUGI_SHADOW },
    })).position.set(pad, 6);

    for (let i = 0; i < statKeys.length; i++) {
      const key = statKeys[i]!;
      const bd = breakdown[key];
      if (!bd) continue;
      const ry = headerH + i * rowH;

      // Format the final value
      let finalStr: string;
      if (bd.unit === '%') {
        finalStr = `${(bd.final * 100).toFixed(1)}%`;
      } else if (bd.unit === 'x') {
        finalStr = `x${bd.final.toFixed(2)}`;
      } else if (bd.unit === 'HP' || bd.unit === '') {
        finalStr = `${Math.round(bd.final)}`;
      } else {
        finalStr = `${bd.final.toFixed(2)}`;
      }
      if (bd.unit && bd.unit !== '%' && bd.unit !== 'x' && bd.unit !== 'HP' && bd.unit !== '') {
        finalStr += ` ${bd.unit}`;
      }
      if (bd.unit === 'HP') finalStr += ' HP';

      // Determine color: green for high bonus, dim for base
      const hasBonus = bd.fromStatPoints !== 0 || bd.fromEquipment !== 0 || bd.fromBuildings !== 0;
      const valColor = hasBonus ? 0x88ccaa : TEXT_SECONDARY;

      const labelText = new Text({
        text: bd.label,
        style: { fontFamily: 'Offside', fontSize: 12, fill: TEXT_PRIMARY },
      });
      labelText.position.set(pad, ry);
      panel.addChild(labelText);

      const valText = new Text({
        text: finalStr,
        style: { fontFamily: 'Offside', fontSize: 12, fill: valColor },
      });
      valText.anchor.set(1, 0);
      valText.position.set(panelW - pad, ry);
      panel.addChild(valText);

      // Hoverable row for breakdown tooltip
      const rowHit = new Graphics();
      rowHit.rect(0, ry, panelW, rowH);
      rowHit.fill({ color: 0x000000, alpha: 0.001 });
      rowHit.eventMode = 'static'; rowHit.cursor = 'help';
      rowHit.on('pointerenter', () => {
        const lines: StatBreakdownLine[] = [];
        if (bd.format === 'add') {
          lines.push({ source: 'Base', value: `${bd.base}` });
          if (bd.fromStatPoints !== 0) lines.push({ source: 'Stat points', value: `+${bd.fromStatPoints}` });
          if (bd.fromEquipment !== 0) lines.push({ source: 'Equipment', value: `+${Number(bd.fromEquipment).toFixed(1)}` });
          if (bd.fromBuildings !== 0) lines.push({ source: 'Buildings', value: `+${bd.fromBuildings}` });
        } else if (bd.format === 'multiply') {
          lines.push({ source: 'Base', value: `${bd.base}` });
          if (bd.fromStatPoints !== 1) lines.push({ source: 'Stat points', value: `x${Number(bd.fromStatPoints).toFixed(3)}` });
          if (bd.fromEquipment !== 1 && bd.fromEquipment !== 0) lines.push({ source: 'Equipment', value: `x${Number(bd.fromEquipment).toFixed(3)}` });
          if (bd.fromBuildings !== 1 && bd.fromBuildings !== 0) lines.push({ source: 'Buildings', value: `x${Number(bd.fromBuildings).toFixed(3)}` });
        } else {
          // DR: show each layer
          if (bd.fromStatPoints > 0) lines.push({ source: 'Shield stat', value: `${(bd.fromStatPoints * 100).toFixed(1)}%` });
          if (bd.fromEquipment > 0) lines.push({ source: 'Equipment', value: `${(Number(bd.fromEquipment) * 100).toFixed(1)}%` });
          if (bd.fromBuildings > 0) lines.push({ source: 'Buildings', value: `${(bd.fromBuildings * 100).toFixed(1)}%` });
          lines.push({ source: 'Combined (multiplicative)', value: `${(bd.final * 100).toFixed(1)}%` });
        }

        const { tip, width, height } = createStatBreakdownTooltip(bd.label, finalStr, lines, valColor);
        this.showGenericTooltip(tip, width, height, panelX, panelY + ry);
      });
      rowHit.on('pointerleave', () => this.hideTooltip());
      panel.addChild(rowHit);
    }

    this.container.addChild(panel);
  }

  // ─── Inventory Grid ─────────────────────────────────────
  private buildInventoryGrid(opts?: { x?: number; y?: number; w?: number; h?: number }) {
    const app = this.manager.app;
    const ups = this.manager.upgrades;
    const upInv = ups.inventory;
    const wpInv = ups.weaponInventory;

    const gridW = INV_COLS * (INV_CELL + INV_GAP) - INV_GAP;
    const panelX = opts?.x ?? colRightX(app.screen.width);
    const panelW = opts?.w ?? RIGHT_W;
    const usableW = panelW - SCROLLBAR_WIDTH - 4;
    const gridX = panelX + (usableW - gridW) / 2;
    const panelTopY = opts?.y ?? (CONTENT_Y + this.robotStatsPanelHeight + GAP);
    const gridY = panelTopY + 24;
    const panelH = opts?.h ?? (bottomY(app.screen.height) - GAP - panelTopY);

    const panelBg = new Graphics();
    drawStandardPanel(panelBg, panelX, panelTopY, panelW, panelH);
    this.container.addChild(panelBg);

    this.inventoryHitArea = { x: panelX, y: panelTopY, w: panelW, h: panelH };

    const totalItems = upInv.length + wpInv.length;
    const invBand = new Graphics();
    drawHeaderBand(invBand, panelX, gridY - 22, panelW, ACCENT_DARK, PANEL_FILL, 24);
    this.container.addChild(invBand);
    this.container.addChild(new Text({ text: `INVENTORY (${totalItems})`, style: { ...FONT_SUBHEADER, fill: TEXT_PRIMARY, dropShadow: GUGI_SHADOW } })).position.set(gridX, gridY - 19);

    // Scrollable content area — starts below header band, ends at panel bottom
    const contentTopY = gridY + 4;
    const contentBotY = gridY - 24 + panelH;
    const visibleH = contentBotY - contentTopY;

    const invContent = new Container();
    invContent.eventMode = 'static';
    this.container.addChild(invContent);
    this.invContent = invContent;

    // Transparent hit-area inside invContent so pointer/wheel events fire on
    // gaps between cells. Cell events also bubble up to invContent.
    const invHit = new Graphics();
    invHit.rect(panelX, contentTopY, panelW, visibleH);
    invHit.fill({ color: 0x000000, alpha: 0.001 });
    invHit.eventMode = 'static';
    invContent.addChildAt(invHit, 0);

    let cellIndex = 0;

    // Section header height — same band geometry the WEAPONS divider uses,
    // so PARTS / WEAPONS line up visually when both sections are present.
    const sectionHeaderH = 26;

    // Parts (upgrades)
    const partsHeaderH = upInv.length > 0 ? sectionHeaderH : 0;

    if (upInv.length > 0) {
      const partsBand = new Graphics();
      drawHeaderBand(partsBand, panelX, gridY + 4, panelW, 0x2a1a10, PANEL_FILL, 22);
      invContent.addChild(partsBand);
      invContent.addChild(new Text({ text: 'PARTS', style: { ...FONT_SUBHEADER, fill: TEXT_DIM, dropShadow: GUGI_SHADOW } })).position.set(gridX, gridY + 8);

      const catOrder = { engine: 0, sensors: 1, capacitor: 2, shield: 3, chassis: 4 } as const;
      const sorted = [...upInv]
        .map(qi => ({ qi, def: getUpgradeById(qi.id) }))
        .filter((d): d is { qi: ScoredItem; def: UpgradeDef } => d.def !== undefined)
        .sort((a, b) => b.def.tier - a.def.tier || catOrder[a.def.category] - catOrder[b.def.category]);

      for (const { qi, def } of sorted) {
        const col = cellIndex % INV_COLS, row = Math.floor(cellIndex / INV_COLS);
        this.buildUpgradeCell(def, qi, gridX + col * (INV_CELL + INV_GAP), gridY + partsHeaderH + row * (INV_CELL + INV_GAP), invContent);
        cellIndex++;
      }
    }

    // Weapons
    let weaponYOffset = 0;

    if (wpInv.length > 0) {
      if (upInv.length > 0) {
        const remainder = cellIndex % INV_COLS;
        if (remainder > 0) cellIndex += INV_COLS - remainder;
        const divY = gridY + partsHeaderH + Math.floor(cellIndex / INV_COLS) * (INV_CELL + INV_GAP);
        const divBand = new Graphics();
        drawHeaderBand(divBand, panelX, divY + 4, panelW, 0x2a1a10, PANEL_FILL, 22);
        invContent.addChild(divBand);
        invContent.addChild(new Text({ text: 'WEAPONS', style: { ...FONT_SUBHEADER, fill: TEXT_DIM, dropShadow: GUGI_SHADOW } })).position.set(gridX, divY + 8);
        weaponYOffset = sectionHeaderH;
      }

      const wcOrder = { ballistic: 0, energy: 1, rocket: 2, bomb: 3, melee: 4 } as const;
      const wsorted = [...wpInv]
        .map(qi => ({ qi, def: getWeaponById(qi.id) }))
        .filter((d): d is { qi: ScoredItem; def: WeaponDef } => d.def !== undefined)
        .sort((a, b) => b.def.tier - a.def.tier || wcOrder[a.def.category] - wcOrder[b.def.category]);

      for (const { qi, def } of wsorted) {
        const col = cellIndex % INV_COLS, row = Math.floor(cellIndex / INV_COLS);
        this.buildWeaponCell(def, qi, gridX + col * (INV_CELL + INV_GAP), gridY + partsHeaderH + row * (INV_CELL + INV_GAP) + weaponYOffset, invContent);
        cellIndex++;
      }
    }

    if (totalItems === 0) {
      const empty = new Text({ text: 'No items yet.\nExplore to find\nequipment!', style: { ...FONT_BODY, fill: TEXT_DIM, align: 'center' } });
      empty.anchor.set(0.5, 0); empty.position.set(gridX + gridW / 2, gridY + 30);
      invContent.addChild(empty);
    }

    // Calculate total content height and set up scrolling
    const lastRow = Math.ceil(cellIndex / INV_COLS);
    const totalContentH = lastRow * (INV_CELL + INV_GAP) - INV_GAP + partsHeaderH + weaponYOffset;
    const maxScroll = Math.max(0, totalContentH - visibleH);
    this.invMaxScroll = maxScroll;

    // Clamp scroll offset
    if (this.invScrollOffset > maxScroll) this.invScrollOffset = maxScroll;

    // Mask to clip items within panel
    const mask = new Graphics();
    mask.rect(panelX, contentTopY, panelW, visibleH);
    mask.fill(0xffffff);
    this.container.addChild(mask);
    invContent.mask = mask;

    // Apply scroll offset
    invContent.y = -this.invScrollOffset;

    // Scrollbar
    const cellStep = INV_CELL + INV_GAP;
    const scrollBar = createScrollBar({
      trackHeight: visibleH,
      contentHeight: totalContentH,
      visibleHeight: visibleH,
      onChange: (frac) => {
        this.invScrollOffset = Math.round(frac * this.invMaxScroll);
        if (this.invContent) this.invContent.y = -this.invScrollOffset;
      },
    });
    scrollBar.container.position.set(panelX + panelW - SCROLLBAR_WIDTH - 4, contentTopY);
    if (maxScroll > 0) scrollBar.setFraction(this.invScrollOffset / maxScroll);
    this.container.addChild(scrollBar.container);
    this.invScrollBar = scrollBar;

    // Resize invHit so pointer events (gaps + wheel) fire across the full
    // scrollable content — cells also bubble to invContent.
    invHit.clear();
    invHit.rect(panelX, contentTopY, panelW, Math.max(visibleH, totalContentH));
    invHit.fill({ color: 0x000000, alpha: 0.001 });

    // Unified wheel + click-drag + touch-drag scroll. cellStep is unused; the
    // native wheel deltaY gives smooth pixel-perfect scrolling.
    void cellStep;
    this.invScrollDrag?.destroy();
    this.invScrollDrag = attachScrollDrag({
      hitArea: invContent,
      onDelta: (dy: number) => {
        if (this.invMaxScroll <= 0) return;
        this.invScrollOffset = Math.max(0, Math.min(this.invMaxScroll, this.invScrollOffset + dy));
        if (this.invContent) this.invContent.y = -this.invScrollOffset;
        if (this.invScrollBar) this.invScrollBar.setFraction(this.invScrollOffset / this.invMaxScroll);
      },
    });
  }

  // Mobile inventory drawer — wraps buildInventoryGrid in a resizable bottom panel
  // with a drag handle (pointer-drag to resize, tap to toggle collapse).
  private buildInventoryDrawer(opts: {
    x: number; y: number; w: number; h: number;
    handleH: number; minH: number; maxH: number;
    collapsed: boolean;
  }) {
    const { x, y, w, h, handleH, minH, maxH, collapsed } = opts;

    // Drawer panel background spans handle + grid
    const panelBg = new Graphics();
    drawStandardPanel(panelBg, x, y, w, h);
    this.container.addChild(panelBg);

    // Handle bar — draggable, double-taps to collapse/expand
    const handle = new Container();
    handle.position.set(x, y);
    const handleBg = new Graphics();
    handleBg.rect(0, 0, w, handleH);
    handleBg.fill({ color: ACCENT_DARK, alpha: 0.6 });
    handleBg.moveTo(0, handleH);
    handleBg.lineTo(w, handleH);
    handleBg.stroke({ color: ACCENT_DIM, width: 1 });
    // Grip indicator — three short horizontal lines
    const gripY = handleH / 2;
    for (let i = -1; i <= 1; i++) {
      const gx = w / 2 + i * 14;
      handleBg.moveTo(gx - 5, gripY); handleBg.lineTo(gx + 5, gripY);
    }
    handleBg.stroke({ color: TEXT_PRIMARY, width: 2, alpha: 0.7 });
    handle.addChild(handleBg);

    // Collapsed-state label — visible only when drawer is collapsed
    if (collapsed) {
      const hint = new Text({
        text: 'INVENTORY  ▲',
        style: { ...FONT_DETAIL, fill: TEXT_PRIMARY, dropShadow: GUGI_SHADOW },
      });
      hint.anchor.set(0.5, 0.5);
      hint.position.set(w / 2, handleH / 2 - 10);
      handle.addChild(hint);
    }

    handle.eventMode = 'static';
    handle.cursor = 'ns-resize';
    handle.on('pointerdown', (e: FederatedPointerEvent) => {
      this.drawerDrag = {
        startY: e.globalY,
        startH: h,
        minH,
        maxH,
        dragged: false,
        previewLine: null,
        panelX: x,
        panelW: w,
      };
      e.stopPropagation();
    });
    this.container.addChild(handle);

    // Inventory grid fills the drawer below the handle. Skip rendering when
    // collapsed to save cycles — the handle alone stays interactive.
    if (!collapsed) {
      this.buildInventoryGrid({
        x,
        y: y + handleH,
        w,
        h: h - handleH,
      });
    } else {
      // Still register a hit area for drop-to-inventory when collapsed, so
      // dropping on the handle unequips items back to inventory.
      this.inventoryHitArea = { x, y, w, h };
    }

  }

  private buildUpgradeCell(def: UpgradeDef, item: ScoredItem, cx: number, cy: number, parent: Container) {
    const cell = sharedUpgradeCell(def, item);
    cell.position.set(cx, cy);
    cell.eventMode = 'static'; cell.cursor = 'grab';
    const isMobile = this.vp.mode === 'mobile';
    if (isMobile) {
      // Mobile: long-press (250ms) to pick up so a swipe scrolls the list.
      enableLongPress(cell, (e) => {
        if (this.invScrollDrag?.isDragging() || this.invScrollDrag?.wasDragging()) return;
        this.invScrollDrag?.cancelDrag();
        this.startDrag(e, item, 'upgrade', { type: 'upgradeInventory' });
      }, { ms: 250, moveTolerance: 4 });
    } else {
      // Desktop: instant pickup on press — scroll drag still works via gaps.
      cell.on('pointerdown', (e: FederatedPointerEvent) => this.startDrag(e, item, 'upgrade', { type: 'upgradeInventory' }));
    }
    cell.on('pointerenter', () => this.showUpgradeTooltip(def, item, cx, cy));
    cell.on('pointerleave', () => this.hideTooltip());
    enableLongPress(cell, () => this.showUpgradeTooltip(def, item, cx, cy));
    parent.addChild(cell);
  }

  private buildWeaponCell(def: WeaponDef, item: ScoredItem, cx: number, cy: number, parent: Container) {
    const cell = sharedWeaponCell(def, item);
    cell.position.set(cx, cy);
    cell.eventMode = 'static'; cell.cursor = 'grab';
    const isMobile = this.vp.mode === 'mobile';
    if (isMobile) {
      enableLongPress(cell, (e) => {
        if (this.invScrollDrag?.isDragging() || this.invScrollDrag?.wasDragging()) return;
        this.invScrollDrag?.cancelDrag();
        this.startDrag(e, item, 'weapon', { type: 'weaponInventory' });
      }, { ms: 250, moveTolerance: 4 });
    } else {
      cell.on('pointerdown', (e: FederatedPointerEvent) => this.startDrag(e, item, 'weapon', { type: 'weaponInventory' }));
    }
    cell.on('pointerenter', () => this.showWeaponTooltip(def, item, cx, cy));
    cell.on('pointerleave', () => this.hideTooltip());
    enableLongPress(cell, () => this.showWeaponTooltip(def, item, cx, cy));
    parent.addChild(cell);
  }

  // ─── Drag & Drop ────────────────────────────────────────
  private startDrag(e: FederatedPointerEvent, item: ScoredItem, kind: 'upgrade' | 'weapon', source: DragSource) {
    this.hideTooltip();
    this.dragItem = item;
    this.dragItemKind = kind;
    this.dragSource = source;

    const color = qColor(item.score);
    const ghost = new Container();
    const ghostBg = new Graphics();
    ghostBg.roundRect(-INV_CELL / 2, -INV_CELL / 2, INV_CELL, INV_CELL, 4);
    ghostBg.fill({ color: kind === 'weapon' ? 0x201a14 : 0x1a2030, alpha: 0.85 });
    ghostBg.stroke({ color, width: 2 });
    ghost.addChild(ghostBg);

    const icon = new Graphics();
    if (kind === 'weapon') {
      const wdef = getWeaponById(item.id);
      if (wdef) drawWeaponIcon(icon, wdef.category, 28, color);
    } else {
      const udef = getUpgradeById(item.id);
      if (udef) drawCategoryIcon(icon, udef.category, 28, color);
    }
    icon.position.set(-14, -14);
    ghost.addChild(icon);

    ghost.position.set(e.globalX, e.globalY);
    ghost.alpha = 0.85;
    ghost.scale.set(1);
    this.container.addChild(ghost);
    this.dragGhost = ghost;
    this.dragGhostTargetX = e.globalX;
    this.dragGhostTargetY = e.globalY;
    this.dragGhostPopTime = 0;
  }

  private onDragMove(e: FederatedPointerEvent) {
    if (this.drawerDrag) {
      const dd = this.drawerDrag;
      const dy = e.globalY - dd.startY;
      if (Math.abs(dy) > 3) dd.dragged = true;
      const newH = Math.max(dd.minH, Math.min(dd.maxH, dd.startH - dy));
      // Draw a preview line at the prospective new drawer top — we avoid
      // rebuilding mid-drag because that would destroy the handle's event
      // listeners and kill the drag session.
      if (!dd.previewLine) {
        dd.previewLine = new Graphics();
        this.container.addChild(dd.previewLine);
      }
      const previewY = this.vp.h - tabBarH(this.vp) - MARGIN - newH;
      dd.previewLine.clear();
      dd.previewLine.moveTo(dd.panelX, previewY);
      dd.previewLine.lineTo(dd.panelX + dd.panelW, previewY);
      dd.previewLine.stroke({ color: ACCENT, width: 2, alpha: 0.9 });
      return;
    }
    if (this.dragGhost) {
      this.dragGhostTargetX = e.globalX;
      this.dragGhostTargetY = e.globalY;
    }
  }

  private onDragEnd(e: FederatedPointerEvent) {
    if (this.drawerDrag) {
      const dd = this.drawerDrag;
      this.drawerDrag = null;
      if (dd.previewLine) {
        this.container.removeChild(dd.previewLine);
        dd.previewLine.destroy();
      }
      if (dd.dragged) {
        const dy = e.globalY - dd.startY;
        const newH = Math.max(dd.minH, Math.min(dd.maxH, dd.startH - dy));
        BaseState.invDrawerHeight = newH;
        BaseState.invDrawerCollapsed = newH <= dd.minH + 2;
      } else {
        // Tap with no drag → toggle collapsed state
        BaseState.invDrawerCollapsed = !BaseState.invDrawerCollapsed;
      }
      BaseState.persistInvDrawer();
      this.rebuildUI();
      return;
    }
    if (!this.dragGhost || !this.dragItem || !this.dragSource) return;

    const item = this.dragItem;
    const kind = this.dragItemKind;
    const source = this.dragSource;
    const px = e.globalX, py = e.globalY;

    this.container.removeChild(this.dragGhost);
    this.dragGhost.destroy({ children: true });
    this.dragGhost = null;
    this.dragItem = null;
    this.dragItemKind = null;
    this.dragSource = null;

    const ups = this.manager.upgrades;

    if (kind === 'upgrade') {
      const def = getUpgradeById(item.id);
      if (!def) { this.rebuildUI(); return; }
      if (source.type === 'upgradeInventory') {
        for (const slot of this.slotHitAreas) {
          if (this.hitTest(px, py, slot) && slot.category === def.category) {
            ups.equip(item);
            this.rebuildUI(); return;
          }
        }
      }
      if (source.type === 'upgradeSlot' && this.inventoryHitArea && this.hitTest(px, py, this.inventoryHitArea)) {
        ups.unequip(source.category);
        this.rebuildUI(); return;
      }
    } else if (kind === 'weapon') {
      if (source.type === 'weaponInventory') {
        for (const wslot of this.weaponSlotHitAreas) {
          if (this.hitTest(px, py, wslot)) {
            ups.equipWeapon(wslot.index, item);
            this.rebuildUI(); return;
          }
        }
      } else if (source.type === 'weaponSlot') {
        for (const wslot of this.weaponSlotHitAreas) {
          if (this.hitTest(px, py, wslot) && wslot.index !== source.index) {
            ups.unequipWeapon(source.index);
            ups.equipWeapon(wslot.index, item);
            this.rebuildUI(); return;
          }
        }
      }
      if (source.type === 'weaponSlot' && this.inventoryHitArea && this.hitTest(px, py, this.inventoryHitArea)) {
        ups.unequipWeapon(source.index);
        this.rebuildUI(); return;
      }
    }
    this.rebuildUI();
  }

  private hitTest(px: number, py: number, area: { x: number; y: number; w: number; h: number }): boolean {
    return px >= area.x && px <= area.x + area.w && py >= area.y && py <= area.y + area.h;
  }

  // ─── Tooltips ───────────────────────────────────────────
  private showUpgradeTooltip(def: UpgradeDef, item: ScoredItem, cellX: number, cellY: number) {
    this.hideTooltip();
    const { tip, width, height } = createUpgradeTooltip(def, item);
    const app = this.manager.app;
    positionTooltip(tip, height, width, cellX, cellY, app.screen.width, app.screen.height);
    this.container.addChild(tip);
    this.tooltip = tip;
    this.armTooltipAutoDismiss();
  }

  private showWeaponTooltip(def: WeaponDef, item: ScoredItem, cellX: number, cellY: number) {
    this.hideTooltip();
    const { tip, width, height } = createWeaponTooltip(def, item);
    const app = this.manager.app;
    positionTooltip(tip, height, width, cellX, cellY, app.screen.width, app.screen.height);
    this.container.addChild(tip);
    this.tooltip = tip;
    this.armTooltipAutoDismiss();
  }

  private showGenericTooltip(tip: Container, tipW: number, tipH: number, cellX: number, cellY: number) {
    this.hideTooltip();
    const app = this.manager.app;
    positionTooltip(tip, tipH, tipW, cellX, cellY, app.screen.width, app.screen.height);
    this.container.addChild(tip);
    this.tooltip = tip;
    this.armTooltipAutoDismiss();
  }

  private hideTooltip() {
    if (this.tooltip) { this.container.removeChild(this.tooltip); this.tooltip.destroy({ children: true }); this.tooltip = null; }
    if (this.tooltipDismissDisposer) { this.tooltipDismissDisposer(); this.tooltipDismissDisposer = null; }
  }

  /**
   * Register a one-shot pointerdown listener on the main stage so the next
   * tap anywhere dismisses a tooltip shown via long-press (pointerenter /
   * pointerleave never fires on touch). Called from every show* path.
   */
  private armTooltipAutoDismiss() {
    if (this.tooltipDismissDisposer) { this.tooltipDismissDisposer(); }
    const stage = this.manager.app.stage;
    // Swallow the very first pointerdown after the long-press: we don't want
    // to dismiss on the same gesture that opened it.
    let armed = false;
    const arm = () => { armed = true; };
    const fire = () => {
      if (armed) { this.hideTooltip(); }
    };
    // requestAnimationFrame defers arming to the next frame — past the
    // long-press pointerdown that opened the tooltip.
    const raf = requestAnimationFrame(arm);
    stage.on('pointerdown', fire);
    this.tooltipDismissDisposer = () => {
      cancelAnimationFrame(raf);
      stage.off('pointerdown', fire);
    };
  }

  // ─── Helpers ────────────────────────────────────────────
  private rebuildPending = false;

  private rebuildUI() {
    // Defer rebuild to avoid destroying the container while PixiJS is still
    // processing the pointer event that triggered this (bubbling through
    // a destroyed display-object tree corrupts the internal event state).
    if (this.rebuildPending) return;
    this.rebuildPending = true;
    queueMicrotask(() => {
      this.rebuildPending = false;
      this.hideTooltip();
      this.logoDetail = null;
      this.manager.app.stage.removeChild(this.container);
      this.container.destroy({ children: true });
      this.container = new Container();
      // uiContainer was destroyed recursively by the line above — recreate it
      // before enter() re-adds and populates it, otherwise stale child
      // Text/Graphics from the previous build (e.g. slot item name labels)
      // can linger and overlap the new UI.
      this.uiContainer = new Container();
      this.slotHitAreas = []; this.weaponSlotHitAreas = [];
      this.inventoryHitArea = null;
      this.dragGhost = null; this.dragItem = null; this.dragItemKind = null; this.dragSource = null;
      this.traderBtn = null; this.traderOverlay = null; this.traderTimerText = null;
      this.forgeOverlay = null;
      this.invContent = null; this.invScrollBar = null;
      this.enter();
    });
  }

  private countWalkableTiles(): number {
    const lab = this.manager.labyrinth;
    if (!lab) return 0;
    let count = 0;
    for (let y = 0; y < lab.height; y++) for (let x = 0; x < lab.width; x++) if (lab.isWalkable(x, y)) count++;
    return count;
  }


  // Compact top-bar buttons for mobile: Trader / Buildings / Forge,
  // laid out right-to-left ending just left of the settings button.
  private buildMobileTopButtons() {
    const app = this.manager.app;
    const settingsX = app.screen.width - 46;
    const btnY = 10 + this.vp.safeTop;
    const btnW = 64, btnH = 32;
    const gap = 6;
    let x = settingsX - gap;

    const hasForge = this.manager.buildings.getForgeLevel() > 0;
    const hasTradehub = this.manager.buildings.getBuildingLevel('tradehub') > 0;

    const makeBtn = (label: string, fill: number, textColor: number, onTap: () => void): Container => {
      x -= btnW;
      const btn = new Container();
      btn.position.set(x, btnY);
      const bg = new Graphics();
      draw3DButton(bg, btnW, btnH, fill);
      btn.addChild(bg);
      const txt = new Text({ text: label, style: { ...FONT_DETAIL, fill: textColor, dropShadow: GUGI_SHADOW } });
      txt.anchor.set(0.5); txt.position.set(btnW / 2, btnH / 2);
      btn.addChild(txt);
      btn.eventMode = 'static'; btn.cursor = 'pointer';
      btn.on('pointerover', () => { bg.tint = 0xcccccc; });
      btn.on('pointerout',  () => { bg.tint = 0xffffff; });
      btn.on('pointerdown', onTap);
      this.container.addChild(btn);
      x -= gap;
      return btn;
    };

    if (hasForge) makeBtn('FORGE', 0x884411, 0xffcc88, () => this.openForge());
    makeBtn('BUILD', 0x1a6633, 0xffffff, () => this.openBuildings());
    if (hasTradehub) {
      this.traderBtn = makeBtn('TRADE', ACCENT_MED, 0xffffff, () => this.openTrader());
    }
    makeBtn('AWARDS', 0x553388, 0xddbbff, () => this.openAchievements());
  }

  // ─── Trader ──────────────────────────────────────────────
  private buildTraderButton(opts?: { y?: number }) {
    const app = this.manager.app;
    const btnW = 140, btnH = 34;
    const btnY = opts?.y ?? bottomY(app.screen.height);

    const btn = new Container();
    // position set later in buildBuildingButtons to center the group
    btn.position.set(0, btnY);
    const btnBg = new Graphics();
    draw3DButton(btnBg, btnW, btnH, ACCENT_MED);
    btn.addChild(btnBg);
    const btnText = new Text({ text: 'TRADER', style: { ...FONT_SUBHEADER, fill: 0xffffff, dropShadow: GUGI_SHADOW } });
    btnText.anchor.set(0.5); btnText.position.set(btnW / 2, btnH / 2);
    btn.addChild(btnText);
    btn.eventMode = 'static'; btn.cursor = 'pointer';
    btn.on('pointerover', () => { btnBg.tint = 0xcccccc; });
    btn.on('pointerout',  () => { btnBg.tint = 0xffffff; });
    btn.on('pointerdown', () => this.openTrader());
    this.container.addChild(btn);
    this.traderBtn = btn;

    // Timer text — positioned below the button
    const formatTime = (ms: number) => {
      const totalSec = Math.max(0, Math.ceil(ms / 1000));
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    };
    const timer = new Text({
      text: `restock ${formatTime(this.manager.trader.timeUntilRefresh())}`,
      style: { ...FONT_DETAIL, fill: TEXT_SECONDARY },
    });
    timer.anchor.set(0.5, 0);
    timer.position.set(btnW / 2, btnH + 2);
    btn.addChild(timer);
    this.traderTimerText = timer;
  }

  // ─── Buildings & Forge ───────────────────────────────────
  private buildBuildingButtons(opts?: { cx?: number; y?: number }) {
    const app = this.manager.app;
    const btnY = opts?.y ?? bottomY(app.screen.height);
    const gap = GAP;
    const hasForge = this.manager.buildings.getForgeLevel() > 0;

    // BUILDINGS button
    const bldBtnW = 140, bldBtnH = 34;
    const bldBtn = new Container();
    bldBtn.position.set(0, btnY);
    const bldBg = new Graphics();
    draw3DButton(bldBg, bldBtnW, bldBtnH, 0x1a6633);
    bldBtn.addChild(bldBg);
    const bldText = new Text({ text: 'BUILDINGS', style: { ...FONT_SUBHEADER, fill: 0xffffff, dropShadow: GUGI_SHADOW } });
    bldText.anchor.set(0.5); bldText.position.set(bldBtnW / 2, bldBtnH / 2);
    bldBtn.addChild(bldText);
    bldBtn.eventMode = 'static'; bldBtn.cursor = 'pointer';
    bldBtn.on('pointerover', () => { bldBg.tint = 0xcccccc; });
    bldBtn.on('pointerout',  () => { bldBg.tint = 0xffffff; });
    bldBtn.on('pointerdown', () => this.openBuildings());
    this.container.addChild(bldBtn);

    // FORGE button — only if forge is built
    let forgeBtn: Container | null = null;
    const forgeBtnW = 140, forgeBtnH = 34;
    if (hasForge) {
      forgeBtn = new Container();
      forgeBtn.position.set(0, btnY);
      const forgeBg = new Graphics();
      draw3DButton(forgeBg, forgeBtnW, forgeBtnH, 0x884411);
      forgeBtn.addChild(forgeBg);
      const forgeText = new Text({ text: 'FORGE', style: { ...FONT_SUBHEADER, fill: 0xffcc88, dropShadow: GUGI_SHADOW } });
      forgeText.anchor.set(0.5); forgeText.position.set(forgeBtnW / 2, forgeBtnH / 2);
      forgeBtn.addChild(forgeText);
      forgeBtn.eventMode = 'static'; forgeBtn.cursor = 'pointer';
      forgeBtn.on('pointerover', () => { forgeBg.tint = 0xcccccc; });
      forgeBtn.on('pointerout',  () => { forgeBg.tint = 0xffffff; });
      forgeBtn.on('pointerdown', () => this.openForge());
      this.container.addChild(forgeBtn);
    }

    // Center the button group horizontally in center column
    const traderBtnW = 140;
    const hasTrader = this.traderBtn !== null;
    const totalW =
      bldBtnW +
      (hasForge ? gap + forgeBtnW : 0) +
      (hasTrader ? gap + traderBtnW : 0);
    const colCX = opts?.cx ?? (colCenterX() + centerW(app.screen.width) / 2);
    let cursor = colCX - totalW / 2;

    if (hasForge && forgeBtn) {
      forgeBtn.x = cursor;
      cursor += forgeBtnW + gap;
    }
    bldBtn.x = cursor;
    cursor += bldBtnW + gap;
    if (hasTrader) {
      this.traderBtn!.x = cursor;
    }
  }

  private openBuildings() {
    if (this.forgeOverlay || this.traderOverlay) return;
    this.manager.switchTo(new BuildingState(this.manager));
  }

  private openAchievements() {
    if (this.forgeOverlay || this.traderOverlay || this.achievementsOverlay) return;
    const app = this.manager.app;
    this.achievementsOverlay = buildAchievementsOverlay(
      this.manager, app.screen.width, app.screen.height,
      () => this.closeAchievements(),
    );
    this.container.addChild(this.achievementsOverlay.container);
  }

  private closeAchievements() {
    if (!this.achievementsOverlay) return;
    this.achievementsOverlay.dispose?.();
    this.container.removeChild(this.achievementsOverlay.container);
    this.achievementsOverlay.container.destroy({ children: true });
    this.achievementsOverlay = null;
  }

  private openForge() {
    if (this.forgeOverlay || this.traderOverlay) return;
    const app = this.manager.app;
    this.forgeOverlay = buildForgeOverlay(
      this.manager, app.screen.width, app.screen.height,
      () => this.closeForge(),
    );
    this.container.addChild(this.forgeOverlay.container);
  }

  private closeForge() {
    if (!this.forgeOverlay) return;
    this.forgeOverlay.dispose?.();
    this.container.removeChild(this.forgeOverlay.container);
    this.forgeOverlay.container.destroy({ children: true });
    this.forgeOverlay = null;
    this.manager.save();
    this.rebuildUI();
  }

  private openTrader() {
    if (this.traderOverlay) return;
    this.manager.trader.refreshIfNeeded(this.manager.getExplorationPct());
    const app = this.manager.app;
    const result = buildTraderOverlay(
      this.manager,
      app.screen.width,
      app.screen.height,
      () => this.closeTrader(),
      () => this.refreshTraderOverlay(),
    );
    this.container.addChild(result.container);
    this.traderOverlay = result;
  }

  private closeTrader() {
    if (!this.traderOverlay) return;
    this.traderOverlay.dispose?.();
    this.container.removeChild(this.traderOverlay.container);
    this.traderOverlay.container.destroy({ children: true });
    this.traderOverlay = null;
    this.manager.save();
    this.rebuildUI();
  }

  private refreshTraderOverlay() {
    if (!this.traderOverlay) return;
    this.traderOverlay.dispose?.();
    this.container.removeChild(this.traderOverlay.container);
    this.traderOverlay.container.destroy({ children: true });
    this.traderOverlay = null;
    // Re-open with updated state
    const app = this.manager.app;
    const result = buildTraderOverlay(
      this.manager,
      app.screen.width,
      app.screen.height,
      () => this.closeTrader(),
      () => this.refreshTraderOverlay(),
    );
    this.container.addChild(result.container);
    this.traderOverlay = result;
  }

  update(dt: number) {
    this.toastOverlay?.update(dt);

    // Pickup feedback: scale-pop + lag-follow cursor for the drag ghost.
    if (this.dragGhost) {
      const POP_DURATION = 0.08;
      const POP_MAX = 1.15;
      if (this.dragGhostPopTime < POP_DURATION) {
        this.dragGhostPopTime = Math.min(POP_DURATION, this.dragGhostPopTime + dt);
        const t = this.dragGhostPopTime / POP_DURATION;
        const eased = 1 - (1 - t) * (1 - t);
        this.dragGhost.scale.set(1 + (POP_MAX - 1) * eased);
      }
      // Frame-rate-independent lerp toward pointer (k ≈ 25 → ~33% per 60fps frame).
      const k = 1 - Math.exp(-25 * dt);
      this.dragGhost.position.x += (this.dragGhostTargetX - this.dragGhost.position.x) * k;
      this.dragGhost.position.y += (this.dragGhostTargetY - this.dragGhost.position.y) * k;
    }

    // Fade-in transition for UI elements
    if (this.transitionIn && this.uiContainer.alpha < 1) {
      this.fadeInTime += dt;
      this.uiContainer.alpha = Math.min(1, this.fadeInTime / BaseState.FADE_IN_DURATION);
      if (this.uiContainer.alpha >= 1) {
        this.transitionIn = false;
      }
    }

    this.logoTime += dt;
    if (this.logoDetail) {
      this.logoDetail.rotation = this.logoTime * 0.5;
      const pulse = this.logoBaseScale * (1 + Math.sin(this.logoTime * 1.2) * 0.08);
      this.logoDetail.scale.set(pulse);
    }

    // ── Background animation ──
    this.bgTime += dt;

    // Gentle circuit alpha breathing
    if (this.circuitGfx) {
      this.circuitGfx.alpha = this.circuitBaseAlpha * (1 + Math.sin(this.bgTime * 0.3) * 0.08);
    }

    // Scan line sweep (slow horizontal sweep, ~12s period)
    if (this.scanLine) {
      const W = this.manager.app.screen.width;
      const H = this.manager.app.screen.height;
      const period = 12;
      const phase = (this.bgTime % period) / period; // 0→1
      const scanX = phase * (W + 200) - 100;

      // Fade in at start, fade out at end
      const edgeFade = Math.min(phase * 5, (1 - phase) * 5, 1);

      this.scanLine.clear();
      // Gradient-like scan band using multiple lines
      for (let i = -30; i <= 30; i++) {
        const dist = Math.abs(i) / 30;
        const a = (1 - dist * dist) * 0.035 * edgeFade;
        this.scanLine.moveTo(scanX + i, 0);
        this.scanLine.lineTo(scanX + i, H);
        this.scanLine.stroke({ color: ACCENT, width: 1, alpha: a });
      }
      this.scanLine.alpha = 1;
    }

    // Energy pulses along traces
    if (this.pulseGfx && this.circuitSegments.length > 0) {
      this.pulseGfx.clear();
      const TIERS_COLORS = [0xDD2233, 0xBB1122, 0x881122];
      const TIERS_ALPHA = [0.7, 0.5, 0.35];

      for (const p of this.pulses) {
        p.t += p.speed * dt;
        if (p.t > 1) {
          // Jump to a connected or random segment
          p.t -= 1;
          p.segIdx = (p.segIdx + 1 + Math.floor(Math.random() * 3)) % this.circuitSegments.length;
          p.tier = this.circuitSegments[p.segIdx]!.tier;
        }
        const seg = this.circuitSegments[p.segIdx]!;
        const x = seg.x1 + (seg.x2 - seg.x1) * p.t;
        const y = seg.y1 + (seg.y2 - seg.y1) * p.t;
        const col = TIERS_COLORS[p.tier]!;
        const a = TIERS_ALPHA[p.tier]!;

        // Glow
        this.pulseGfx.circle(x, y, 6);
        this.pulseGfx.fill({ color: col, alpha: a * 0.25 });
        // Core dot
        this.pulseGfx.circle(x, y, 2);
        this.pulseGfx.fill({ color: col, alpha: a });
      }
    }

    // Update trader timer
    if (this.traderTimerText) {
      const ms = this.manager.trader.timeUntilRefresh();
      const totalSec = Math.max(0, Math.ceil(ms / 1000));
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      this.traderTimerText.text = `restock ${m}:${s.toString().padStart(2, '0')}`;
    }
    if (this.traderOverlay) {
      this.traderOverlay.updateTimer();
      // Auto-refresh if stock restocked while overlay is open
      if (this.manager.trader.refreshIfNeeded(this.manager.getExplorationPct())) {
        this.refreshTraderOverlay();
      }
    }
    this.forgeOverlay?.update(dt);
  }

  exit() {
    this.hideTooltip();
    this.logoDetail = null;
    this.circuitGfx = null;
    this.scanLine = null;
    this.pulseGfx = null;
    this.circuitSegments = [];
    this.pulses = [];
    this.toastOverlay = null;
    if (this.vpUnsub) { this.vpUnsub(); this.vpUnsub = null; }
    this.manager.app.stage.removeChild(this.container);
    this.container.destroy({ children: true });
  }
}
